import type {
  CreateInventoryCategoryRequest,
  CreateInventoryCheckoutRequest,
  CreateInventoryItemRequest,
  CreateInventoryLocationRequest,
  CreateInventoryMovementRequest,
  CreateInventorySupplierRequest,
  CreateMaintenanceRecordRequest,
  InventoryAlertsResponse,
  InventoryCategoryDetailResponse,
  InventoryCategorySummaryResponse,
  InventoryCheckoutResponse,
  InventoryItemDetailResponse,
  InventoryItemSummaryResponse,
  InventoryItemType,
  InventoryLocationDetailResponse,
  InventoryLocationSummaryResponse,
  InventoryMovementResponse,
  InventorySupplierDetailResponse,
  InventorySupplierSummaryResponse,
  ItemCondition,
  InventoryCheckoutStatus,
  MaintenanceRecordResponse,
  MaintenanceType,
  PaginatedResponse,
  ReportLostInventoryCheckoutRequest,
  ReturnInventoryCheckoutRequest,
  UpdateInventoryCategoryRequest,
  UpdateInventoryItemRequest,
  UpdateInventoryLocationRequest,
  UpdateInventorySupplierRequest,
  UpdateMaintenanceRecordRequest,
} from '@tabita/contracts';
import { apiRequest } from './api-client';

export interface ListInventoryItemsParams {
  readonly search?: string | undefined;
  readonly categoryId?: string | undefined;
  readonly locationId?: string | undefined;
  readonly supplierId?: string | undefined;
  readonly type?: InventoryItemType | undefined;
  readonly lowStockOnly?: boolean | undefined;
  readonly includeArchived?: boolean | undefined;
  readonly limit?: number | undefined;
  readonly cursor?: string | undefined;
}

export interface ListInventoryLookupParams {
  readonly search?: string | undefined;
  readonly includeArchived?: boolean | undefined;
  readonly limit?: number | undefined;
}

export const inventoryApi = {
  // --- Items ------------------------------------------------------------------

  listItems(params: ListInventoryItemsParams = {}, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<InventoryItemSummaryResponse>>('/inventory/items', {
      query: { ...params },
      signal,
    });
  },

  getItem(id: string, signal?: AbortSignal) {
    return apiRequest<InventoryItemDetailResponse>(`/inventory/items/${id}`, { signal });
  },

  createItem(body: CreateInventoryItemRequest) {
    return apiRequest<InventoryItemDetailResponse>('/inventory/items', { method: 'POST', body });
  },

  updateItem(id: string, body: UpdateInventoryItemRequest) {
    return apiRequest<InventoryItemDetailResponse>(`/inventory/items/${id}`, {
      method: 'PATCH',
      body,
    });
  },

  archiveItem(id: string, reason: string) {
    return apiRequest<InventoryItemDetailResponse>(`/inventory/items/${id}/archive`, {
      method: 'POST',
      body: { reason },
    });
  },

  restoreItem(id: string) {
    return apiRequest<InventoryItemDetailResponse>(`/inventory/items/${id}/restore`, {
      method: 'POST',
    });
  },

  // --- Movements ---------------------------------------------------------------

  listMovements(
    params: { itemId?: string; limit?: number; cursor?: string } = {},
    signal?: AbortSignal,
  ) {
    return apiRequest<PaginatedResponse<InventoryMovementResponse>>('/inventory/movements', {
      query: { ...params },
      signal,
    });
  },

  recordMovement(itemId: string, body: CreateInventoryMovementRequest) {
    return apiRequest<InventoryMovementResponse>(`/inventory/items/${itemId}/movements`, {
      method: 'POST',
      body,
    });
  },

  // --- Checkouts -----------------------------------------------------------------

  listCheckouts(
    params: {
      itemId?: string;
      borrowerPersonId?: string;
      status?: InventoryCheckoutStatus;
      overdueOnly?: boolean;
      limit?: number;
      cursor?: string;
    } = {},
    signal?: AbortSignal,
  ) {
    return apiRequest<PaginatedResponse<InventoryCheckoutResponse>>('/inventory/checkouts', {
      query: { ...params },
      signal,
    });
  },

  checkOut(itemId: string, body: CreateInventoryCheckoutRequest) {
    return apiRequest<InventoryCheckoutResponse>(`/inventory/items/${itemId}/checkouts`, {
      method: 'POST',
      body,
    });
  },

  returnCheckout(id: string, body: ReturnInventoryCheckoutRequest) {
    return apiRequest<InventoryCheckoutResponse>(`/inventory/checkouts/${id}/return`, {
      method: 'POST',
      body,
    });
  },

  reportLostCheckout(id: string, body: ReportLostInventoryCheckoutRequest) {
    return apiRequest<InventoryCheckoutResponse>(`/inventory/checkouts/${id}/report-lost`, {
      method: 'POST',
      body,
    });
  },

  // --- Maintenance ---------------------------------------------------------------

  listMaintenance(
    params: { itemId?: string; type?: MaintenanceType; dueOnly?: boolean; limit?: number } = {},
    signal?: AbortSignal,
  ) {
    return apiRequest<PaginatedResponse<MaintenanceRecordResponse>>('/inventory/maintenance', {
      query: { ...params },
      signal,
    });
  },

  createMaintenance(itemId: string, body: CreateMaintenanceRecordRequest) {
    return apiRequest<MaintenanceRecordResponse>(`/inventory/items/${itemId}/maintenance`, {
      method: 'POST',
      body,
    });
  },

  updateMaintenance(id: string, body: UpdateMaintenanceRecordRequest) {
    return apiRequest<MaintenanceRecordResponse>(`/inventory/maintenance/${id}`, {
      method: 'PATCH',
      body,
    });
  },

  // --- Categories ------------------------------------------------------------

  listCategories(
    params: ListInventoryLookupParams & { parentCategoryId?: string } = {},
    signal?: AbortSignal,
  ) {
    return apiRequest<PaginatedResponse<InventoryCategorySummaryResponse>>(
      '/inventory/categories',
      { query: { ...params }, signal },
    );
  },

  getCategory(id: string, signal?: AbortSignal) {
    return apiRequest<InventoryCategoryDetailResponse>(`/inventory/categories/${id}`, { signal });
  },

  createCategory(body: CreateInventoryCategoryRequest) {
    return apiRequest<InventoryCategoryDetailResponse>('/inventory/categories', {
      method: 'POST',
      body,
    });
  },

  updateCategory(id: string, body: UpdateInventoryCategoryRequest) {
    return apiRequest<InventoryCategoryDetailResponse>(`/inventory/categories/${id}`, {
      method: 'PATCH',
      body,
    });
  },

  archiveCategory(id: string, reason: string) {
    return apiRequest<InventoryCategoryDetailResponse>(`/inventory/categories/${id}/archive`, {
      method: 'POST',
      body: { reason },
    });
  },

  restoreCategory(id: string) {
    return apiRequest<InventoryCategoryDetailResponse>(`/inventory/categories/${id}/restore`, {
      method: 'POST',
    });
  },

  // --- Locations -----------------------------------------------------------------

  listLocations(
    params: ListInventoryLookupParams & { campusId?: string } = {},
    signal?: AbortSignal,
  ) {
    return apiRequest<PaginatedResponse<InventoryLocationSummaryResponse>>('/inventory/locations', {
      query: { ...params },
      signal,
    });
  },

  createLocation(body: CreateInventoryLocationRequest) {
    return apiRequest<InventoryLocationDetailResponse>('/inventory/locations', {
      method: 'POST',
      body,
    });
  },

  updateLocation(id: string, body: UpdateInventoryLocationRequest) {
    return apiRequest<InventoryLocationDetailResponse>(`/inventory/locations/${id}`, {
      method: 'PATCH',
      body,
    });
  },

  archiveLocation(id: string, reason: string) {
    return apiRequest<InventoryLocationDetailResponse>(`/inventory/locations/${id}/archive`, {
      method: 'POST',
      body: { reason },
    });
  },

  restoreLocation(id: string) {
    return apiRequest<InventoryLocationDetailResponse>(`/inventory/locations/${id}/restore`, {
      method: 'POST',
    });
  },

  // --- Suppliers -----------------------------------------------------------------

  listSuppliers(params: ListInventoryLookupParams = {}, signal?: AbortSignal) {
    return apiRequest<PaginatedResponse<InventorySupplierSummaryResponse>>('/inventory/suppliers', {
      query: { ...params },
      signal,
    });
  },

  createSupplier(body: CreateInventorySupplierRequest) {
    return apiRequest<InventorySupplierDetailResponse>('/inventory/suppliers', {
      method: 'POST',
      body,
    });
  },

  updateSupplier(id: string, body: UpdateInventorySupplierRequest) {
    return apiRequest<InventorySupplierDetailResponse>(`/inventory/suppliers/${id}`, {
      method: 'PATCH',
      body,
    });
  },

  archiveSupplier(id: string, reason: string) {
    return apiRequest<InventorySupplierDetailResponse>(`/inventory/suppliers/${id}/archive`, {
      method: 'POST',
      body: { reason },
    });
  },

  restoreSupplier(id: string) {
    return apiRequest<InventorySupplierDetailResponse>(`/inventory/suppliers/${id}/restore`, {
      method: 'POST',
    });
  },

  // --- Alerts ----------------------------------------------------------------

  getAlerts(signal?: AbortSignal) {
    return apiRequest<InventoryAlertsResponse>('/inventory/alerts', { signal });
  },
};

export type { ItemCondition };
